import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Row,
  Col,
  Statistic,
  Tabs,
  Alert,
  Button,
  Spin,
  message,
  InputNumber,
  Select,
  Space,
  Table,
  Popconfirm,
  Tag,
  Input,
  Modal,
  Form,
  Descriptions,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  getPortfolio,
  updatePortfolio,
  getPortfolioSummary,
  getPortfolioHoldings,
  getPortfolioTargetAllocations,
  createPortfolioTargetAllocation,
  updatePortfolioTargetAllocation,
  deletePortfolioTargetAllocation,
  getAllInstruments,
  unitizePortfolio,
  subscribePortfolio,
  redeemPortfolio,
  getPortfolioCapitalFlows,
  settlePortfolioCapitalFlow,
  cancelPortfolioCapitalFlow,
  getPortfolioLiquidity,
} from '../services/api';
import TradeList from './TradeList';
import PortfolioRebalancePanel from './PortfolioRebalancePanel';
import PortfolioPerformanceChart from './PortfolioPerformanceChart';
import { formatDisplayDate } from '../utils/dates';
import { formatMoney, formatUnits, humanizeCode } from '../utils/labels';

const { Title, Text } = Typography;

const formatNumber = (raw, fractionDigits = 2) => {
  if (fractionDigits > 2) return formatUnits(raw, { fractionDigits });
  return formatMoney(raw, { fractionDigits });
};

const PortfolioDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [summary, setSummary] = useState(null);
  const [holdingsData, setHoldingsData] = useState(null);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [allocations, setAllocations] = useState([]);
  const [allocationTotal, setAllocationTotal] = useState(0);
  const [isPartialAllocation, setIsPartialAllocation] = useState(false);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [instrumentLoading, setInstrumentLoading] = useState(false);
  const [instruments, setInstruments] = useState([]);
  const [newInstrumentId, setNewInstrumentId] = useState(null);
  const [newTargetPercentage, setNewTargetPercentage] = useState(null);
  const [savingNewRow, setSavingNewRow] = useState(false);
  const [rowSavingState, setRowSavingState] = useState({});
  const [draftPercentages, setDraftPercentages] = useState({});
  const [draftMinPercentages, setDraftMinPercentages] = useState({});
  const [draftMaxPercentages, setDraftMaxPercentages] = useState({});
  const [newMinPercentage, setNewMinPercentage] = useState(null);
  const [newMaxPercentage, setNewMaxPercentage] = useState(null);
  const [custodianDraft, setCustodianDraft] = useState('mcb');
  const [custodianCodeDraft, setCustodianCodeDraft] = useState('');
  const [savingCustodian, setSavingCustodian] = useState(false);
  const [unitizing, setUnitizing] = useState(false);
  const [capitalModal, setCapitalModal] = useState(null); // 'subscribe' | 'redeem' | null
  const [capitalSaving, setCapitalSaving] = useState(false);
  const [capitalForm] = Form.useForm();
  const [capitalFlows, setCapitalFlows] = useState([]);
  const [capitalFlowsLoading, setCapitalFlowsLoading] = useState(false);
  const [liquidityInfo, setLiquidityInfo] = useState(null);
  const [flowActionBusy, setFlowActionBusy] = useState({});

  const loadHoldings = useCallback(async () => {
    setHoldingsLoading(true);
    try {
      const data = await getPortfolioHoldings(id);
      setHoldingsData(data);
    } catch {
      setHoldingsData(null);
    } finally {
      setHoldingsLoading(false);
    }
  }, [id]);

  const refreshPortfolioAndSummary = useCallback(async () => {
    const [p, s] = await Promise.all([getPortfolio(id), getPortfolioSummary(id)]);
    setPortfolio(p);
    setSummary(s);
    await loadHoldings();
    return { p, s };
  }, [id, loadHoldings]);

  const loadCapitalFlows = useCallback(async () => {
    setCapitalFlowsLoading(true);
    try {
      const data = await getPortfolioCapitalFlows(id);
      setCapitalFlows(data?.items || []);
    } catch {
      setCapitalFlows([]);
    } finally {
      setCapitalFlowsLoading(false);
    }
  }, [id]);

  const loadLiquidity = useCallback(async () => {
    try {
      const data = await getPortfolioLiquidity(id);
      setLiquidityInfo(data);
    } catch {
      setLiquidityInfo(null);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        const [p, s] = await Promise.all([getPortfolio(id), getPortfolioSummary(id)]);
        if (!cancelled) {
          setPortfolio(p);
          setSummary(s);
          setCustodianDraft(p.custodian || 'mcb');
          setCustodianCodeDraft(p.custodian_account_code || '');
        }
        if (!cancelled) {
          await loadHoldings();
        }
      } catch (e) {
        if (!cancelled) message.error('Failed to load portfolio');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [id, loadHoldings]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setInstrumentLoading(true);
      try {
        const list = await getAllInstruments();
        if (!cancelled) setInstruments(list);
      } catch (e) {
        if (!cancelled) message.error('Failed to load instruments');
      } finally {
        if (!cancelled) setInstrumentLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const syncAllocationDrafts = useCallback((items) => {
    setDraftPercentages(
      items.reduce((acc, item) => {
        acc[item.id] = Number(item.target_percentage);
        return acc;
      }, {})
    );
    setDraftMinPercentages(
      items.reduce((acc, item) => {
        acc[item.id] = item.min_percentage != null ? Number(item.min_percentage) : null;
        return acc;
      }, {})
    );
    setDraftMaxPercentages(
      items.reduce((acc, item) => {
        acc[item.id] = item.max_percentage != null ? Number(item.max_percentage) : null;
        return acc;
      }, {})
    );
  }, []);

  const applyAllocationItems = useCallback(
    (items) => {
      const list = Array.isArray(items) ? items : [];
      setAllocations(list);
      const total = list.reduce((sum, item) => sum + Number(item.target_percentage || 0), 0);
      setAllocationTotal(total);
      setIsPartialAllocation(total < 100);
      syncAllocationDrafts(list);
    },
    [syncAllocationDrafts]
  );

  const loadTargetAllocations = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setAllocationLoading(true);
      try {
        const data = await getPortfolioTargetAllocations(id);
        applyAllocationItems(data?.items || []);
        if (data?.total_target_percentage != null) {
          setAllocationTotal(Number(data.total_target_percentage));
        }
        if (data?.is_partial != null) {
          setIsPartialAllocation(Boolean(data.is_partial));
        }
      } catch (e) {
        message.error('Failed to load target allocations');
      } finally {
        if (!silent) setAllocationLoading(false);
      }
    },
    [id, applyAllocationItems]
  );

  useEffect(() => {
    loadTargetAllocations();
  }, [loadTargetAllocations]);

  useEffect(() => {
    if (portfolio?.unitized || summary?.unitized) {
      loadCapitalFlows();
    }
  }, [portfolio?.unitized, summary?.unitized, loadCapitalFlows]);

  const setRowSaving = (allocationId, isSaving) => {
    setRowSavingState((prev) => ({
      ...prev,
      [allocationId]: isSaving,
    }));
  };

  const handleSaveCustodian = async () => {
    try {
      setSavingCustodian(true);
      const updated = await updatePortfolio(id, {
        custodian: custodianDraft,
        custodian_account_code: custodianCodeDraft,
      });
      setPortfolio(updated);
      message.success('Custodian mapping saved');
    } catch (error) {
      const apiError = error?.response?.data;
      if (typeof apiError === 'object') {
        message.error(Object.values(apiError).flat().join(' '));
      } else {
        message.error('Failed to save custodian mapping');
      }
    } finally {
      setSavingCustodian(false);
    }
  };

  const handleUnitize = async () => {
    try {
      setUnitizing(true);
      await unitizePortfolio(id, { create_instrument: true });
      await refreshPortfolioAndSummary();
      message.success('Portfolio unitized — unit NAV is now available for external products');
    } catch (error) {
      const apiError = error?.response?.data;
      message.error(apiError?.error || 'Failed to enable unitization');
    } finally {
      setUnitizing(false);
    }
  };

  const openCapitalModal = (mode) => {
    capitalForm.resetFields();
    setCapitalModal(mode);
    if (mode === 'redeem') {
      loadLiquidity();
    }
  };

  const handleCapitalSubmit = async () => {
    try {
      const values = await capitalForm.validateFields();
      setCapitalSaving(true);
      if (capitalModal === 'subscribe') {
        await subscribePortfolio(id, {
          amount: String(values.amount),
          reference: values.reference || '',
        });
        message.success('Subscription created as pending — settle to increase cash');
      } else {
        const body = { reference: values.reference || '' };
        if (values.redeem_by === 'amount') {
          body.amount = String(values.amount);
        } else {
          body.units = String(values.units);
        }
        const result = await redeemPortfolio(id, body);
        const dealing = result?.effective_date;
        message.success(
          dealing
            ? `Redemption pending for dealing date ${dealing}`
            : 'Redemption created as pending — settle to decrease cash'
        );
      }
      setCapitalModal(null);
      await Promise.all([refreshPortfolioAndSummary(), loadCapitalFlows()]);
    } catch (error) {
      if (error?.errorFields) return;
      const apiError = error?.response?.data;
      message.error(apiError?.error || 'Capital flow failed');
    } finally {
      setCapitalSaving(false);
    }
  };

  const handleSettleFlow = async (flowId) => {
    setFlowActionBusy((prev) => ({ ...prev, [flowId]: true }));
    try {
      await settlePortfolioCapitalFlow(id, flowId);
      message.success('Capital flow settled — cash updated');
      await Promise.all([refreshPortfolioAndSummary(), loadCapitalFlows()]);
    } catch (error) {
      const apiError = error?.response?.data;
      message.error(apiError?.error || 'Failed to settle');
    } finally {
      setFlowActionBusy((prev) => ({ ...prev, [flowId]: false }));
    }
  };

  const handleCancelFlow = async (flowId) => {
    setFlowActionBusy((prev) => ({ ...prev, [flowId]: true }));
    try {
      await cancelPortfolioCapitalFlow(id, flowId);
      message.success('Capital flow cancelled');
      await loadCapitalFlows();
    } catch (error) {
      const apiError = error?.response?.data;
      message.error(apiError?.error || 'Failed to cancel');
    } finally {
      setFlowActionBusy((prev) => ({ ...prev, [flowId]: false }));
    }
  };

  const validateAllocationBand = (target, minPct, maxPct) => {
    if (target == null || Number.isNaN(Number(target))) {
      return 'Target % is required';
    }
    const t = Number(target);
    const minV = minPct == null || minPct === '' ? null : Number(minPct);
    const maxV = maxPct == null || maxPct === '' ? null : Number(maxPct);
    if (minV != null && Number.isFinite(minV) && minV > t) {
      return 'Min % cannot be greater than Target %';
    }
    if (maxV != null && Number.isFinite(maxV) && maxV < t) {
      return 'Max % cannot be less than Target %';
    }
    if (
      minV != null &&
      maxV != null &&
      Number.isFinite(minV) &&
      Number.isFinite(maxV) &&
      minV > maxV
    ) {
      return 'Min % cannot be greater than Max %';
    }
    return null;
  };

  const handleAddAllocation = async () => {
    if (!newInstrumentId || newTargetPercentage == null) {
      message.warning('Select an instrument and enter a target percentage');
      return;
    }
    const bandError = validateAllocationBand(
      newTargetPercentage,
      newMinPercentage,
      newMaxPercentage
    );
    if (bandError) {
      message.warning(bandError);
      return;
    }

    setSavingNewRow(true);
    try {
      await createPortfolioTargetAllocation(id, {
        instrument_id: Number(newInstrumentId),
        target_percentage: Number(newTargetPercentage),
        min_percentage: newMinPercentage != null ? Number(newMinPercentage) : undefined,
        max_percentage: newMaxPercentage != null ? Number(newMaxPercentage) : undefined,
      });
      message.success('Target allocation added');
      setNewInstrumentId(null);
      setNewTargetPercentage(null);
      setNewMinPercentage(null);
      setNewMaxPercentage(null);
      await loadTargetAllocations({ silent: true });
    } catch (error) {
      const apiError = error?.response?.data;
      if (typeof apiError === 'object') {
        message.error(Object.values(apiError).flat().join(' '));
      } else {
        message.error('Failed to add target allocation');
      }
    } finally {
      setSavingNewRow(false);
    }
  };

  const handleSaveAllocation = async (row) => {
    const nextValue = draftPercentages[row.id];
    if (nextValue == null || nextValue === '') {
      message.warning('Target % is required');
      return;
    }
    const bandError = validateAllocationBand(
      nextValue,
      draftMinPercentages[row.id],
      draftMaxPercentages[row.id]
    );
    if (bandError) {
      message.warning(bandError);
      return;
    }

    setRowSaving(row.id, true);
    try {
      const updated = await updatePortfolioTargetAllocation(id, row.id, {
        target_percentage: Number(nextValue),
        min_percentage:
          draftMinPercentages[row.id] != null && draftMinPercentages[row.id] !== ''
            ? Number(draftMinPercentages[row.id])
            : null,
        max_percentage:
          draftMaxPercentages[row.id] != null && draftMaxPercentages[row.id] !== ''
            ? Number(draftMaxPercentages[row.id])
            : null,
      });
      setAllocations((prev) => {
        const next = prev.map((item) =>
          item.id === row.id ? { ...item, ...updated } : item
        );
        const total = next.reduce((sum, item) => sum + Number(item.target_percentage || 0), 0);
        setAllocationTotal(total);
        setIsPartialAllocation(total < 100);
        return next;
      });
      setDraftPercentages((prev) => ({
        ...prev,
        [row.id]: Number(updated.target_percentage),
      }));
      setDraftMinPercentages((prev) => ({
        ...prev,
        [row.id]:
          updated.min_percentage != null ? Number(updated.min_percentage) : null,
      }));
      setDraftMaxPercentages((prev) => ({
        ...prev,
        [row.id]:
          updated.max_percentage != null ? Number(updated.max_percentage) : null,
      }));
      message.success('Allocation rule updated');
    } catch (error) {
      const apiError = error?.response?.data;
      if (typeof apiError === 'object') {
        message.error(Object.values(apiError).flat().join(' '));
      } else {
        message.error('Failed to update allocation rule');
      }
    } finally {
      setRowSaving(row.id, false);
    }
  };

  const handleDeleteAllocation = async (row) => {
    setRowSaving(row.id, true);
    try {
      await deletePortfolioTargetAllocation(id, row.id);
      setAllocations((prev) => {
        const next = prev.filter((item) => item.id !== row.id);
        const total = next.reduce((sum, item) => sum + Number(item.target_percentage || 0), 0);
        setAllocationTotal(total);
        setIsPartialAllocation(total < 100);
        return next;
      });
      setDraftPercentages((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setDraftMinPercentages((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      setDraftMaxPercentages((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      message.success('Target allocation removed');
    } catch (error) {
      message.error('Failed to remove target allocation');
    } finally {
      setRowSaving(row.id, false);
    }
  };

  const usedInstrumentIds = new Set(allocations.map((item) => item.instrument?.id));
  const selectableInstruments = instruments.filter(
    (instrument) => !usedInstrumentIds.has(instrument.id) || instrument.id === newInstrumentId
  );

  const targetByInstrumentId = useMemo(() => {
    const map = {};
    allocations.forEach((row) => {
      const instId = row.instrument?.id;
      if (instId != null) {
        map[instId] = row.target_percentage;
      }
    });
    return map;
  }, [allocations]);

  const reportingCcy =
    summary?.reporting_currency || portfolio?.reporting_currency?.code || '';

  const holdingsTableRows = useMemo(() => {
    const holdings = Array.isArray(holdingsData?.holdings) ? holdingsData.holdings : [];
    const rows = holdings
      .map((h) => ({
        key: `inst-${h.instrument_id}`,
        isCash: false,
        instrument_id: h.instrument_id,
        instrument_name: h.instrument_name,
        isin: h.isin,
        symbol: h.symbol,
        currency: h.currency,
        quantity: h.quantity,
        market_price: h.market_price,
        market_value_base: h.market_value_base,
        actual_weight: h.actual_weight,
        target_percentage: targetByInstrumentId[h.instrument_id] ?? null,
        asset_class: h.asset_class,
      }))
      .sort((a, b) => Number(b.actual_weight || 0) - Number(a.actual_weight || 0));

    if (holdingsData) {
      rows.push({
        key: 'cash',
        isCash: true,
        instrument_name: 'Cash',
        isin: '—',
        quantity: null,
        market_price: null,
        market_value_base: holdingsData.cash_balance,
        actual_weight: holdingsData.cash_weight,
        target_percentage:
          portfolio?.minimum_cash_percentage != null
            ? portfolio.minimum_cash_percentage
            : null,
        currency: reportingCcy,
      });
    }
    return rows;
  }, [holdingsData, targetByInstrumentId, portfolio?.minimum_cash_percentage, reportingCcy]);

  const holdingsColumns = [
    {
      title: 'Instrument',
      key: 'instrument',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong={row.isCash}>{row.instrument_name}</Text>
          {!row.isCash && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.isin || row.symbol || '—'}
              {row.asset_class ? ` · ${row.asset_class}` : ''}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      width: 110,
      render: (v, row) => (row.isCash ? '—' : formatNumber(v, 4)),
    },
    {
      title: 'Price',
      dataIndex: 'market_price',
      key: 'market_price',
      align: 'right',
      width: 110,
      render: (v, row) => (row.isCash ? '—' : formatNumber(v, 2)),
    },
    {
      title: `Value (${reportingCcy || '—'})`,
      dataIndex: 'market_value_base',
      key: 'market_value_base',
      align: 'right',
      width: 140,
      render: (v) => formatNumber(v, 2),
    },
    {
      title: 'Weight %',
      dataIndex: 'actual_weight',
      key: 'actual_weight',
      align: 'right',
      width: 100,
      render: (v) => formatNumber(v, 2),
    },
    {
      title: 'Target %',
      dataIndex: 'target_percentage',
      key: 'target_percentage',
      align: 'right',
      width: 100,
      render: (v, row) =>
        v == null || v === ''
          ? '—'
          : `${formatNumber(v, 2)}${row.isCash ? ' min' : ''}`,
    },
  ];

  const allocationColumns = [
    {
      title: 'Instrument',
      dataIndex: 'instrument',
      key: 'instrument',
      render: (instrument) => (
        <Space direction="vertical" size={0}>
          <Text>{instrument?.name || '-'}</Text>
          <Text type="secondary">
            {instrument?.isin || instrument?.symbol || instrument?.ticker || '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Target %',
      key: 'target_percentage',
      width: 120,
      render: (_, row) => (
        <InputNumber
          min={0}
          max={100}
          step={0.01}
          value={draftPercentages[row.id]}
          onChange={(value) =>
            setDraftPercentages((prev) => ({
              ...prev,
              [row.id]: value,
            }))
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Min %',
      key: 'min_percentage',
      width: 100,
      render: (_, row) => (
        <InputNumber
          min={0}
          max={
            draftPercentages[row.id] != null && draftPercentages[row.id] !== ''
              ? Number(draftPercentages[row.id])
              : 100
          }
          step={0.01}
          value={draftMinPercentages[row.id]}
          onChange={(value) =>
            setDraftMinPercentages((prev) => ({
              ...prev,
              [row.id]: value,
            }))
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Max %',
      key: 'max_percentage',
      width: 100,
      render: (_, row) => (
        <InputNumber
          min={
            draftPercentages[row.id] != null && draftPercentages[row.id] !== ''
              ? Number(draftPercentages[row.id])
              : 0
          }
          max={100}
          step={0.01}
          value={draftMaxPercentages[row.id]}
          onChange={(value) =>
            setDraftMaxPercentages((prev) => ({
              ...prev,
              [row.id]: value,
            }))
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, row) => {
        const numOrNull = (v) => {
          if (v == null || v === '') return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        };
        const sameNum = (a, b) => {
          if (a == null && b == null) return true;
          if (a == null || b == null) return false;
          return Math.abs(a - b) < 1e-9;
        };
        const draftTarget = numOrNull(draftPercentages[row.id]);
        const draftMin = numOrNull(draftMinPercentages[row.id]);
        const draftMax = numOrNull(draftMaxPercentages[row.id]);
        const changed =
          draftTarget != null &&
          (!sameNum(draftTarget, numOrNull(row.target_percentage)) ||
            !sameNum(draftMin, numOrNull(row.min_percentage)) ||
            !sameNum(draftMax, numOrNull(row.max_percentage)));
        const busy = Boolean(rowSavingState[row.id]);
        return (
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={() => handleSaveAllocation(row)}
              disabled={!changed}
              loading={busy}
            >
              Save
            </Button>
            <Popconfirm
              title="Remove allocation rule?"
              onConfirm={() => handleDeleteAllocation(row)}
              okText="Remove"
              cancelText="Cancel"
            >
              <Button size="small" danger loading={busy}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  if (loading || !portfolio || !summary) {
    return (
      <Card>
        <Spin size="large" />
      </Card>
    );
  }

  return (
    <Card>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/portfolios')}
        style={{ paddingLeft: 0 }}
      >
        All portfolios
      </Button>
      <Title level={4}>{portfolio.name}</Title>
      <Text type="secondary">
        Account: {portfolio.account?.name} ({portfolio.account?.account_number})
      </Text>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={summary.unitized || portfolio.unitized ? 6 : 12}>
          <Card size="small">
            <Statistic
              title="Total NAV"
              value={
                summary.total_assets != null
                  ? Number(summary.total_assets)
                  : Number(summary.valuation || 0) + Number(summary.available_cash || 0)
              }
              precision={2}
              suffix={summary.reporting_currency}
            />
          </Card>
        </Col>
        {(summary.unitized || portfolio.unitized) && (
          <>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Unit NAV"
                  value={Number(summary.nav_per_unit || portfolio.base_nav)}
                  precision={2}
                  suffix={summary.reporting_currency}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Units outstanding"
                  value={Number(summary.units_outstanding ?? portfolio.units_outstanding)}
                  precision={4}
                />
              </Card>
            </Col>
          </>
        )}
        <Col xs={24} sm={12} md={summary.unitized || portfolio.unitized ? 6 : 12}>
          <Card size="small">
            <Statistic
              title="Available cash"
              value={Number(summary.available_cash)}
              precision={2}
              suffix={summary.reporting_currency}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        style={{ marginTop: 24 }}
        defaultActiveKey="overview"
        destroyInactiveTabPane={false}
        items={[
          {
            key: 'performance',
            label: 'Performance',
            children: (
              <PortfolioPerformanceChart
                portfolioId={id}
                riskProfile={portfolio?.risk_profile}
              />
            ),
          },
          {
            key: 'overview',
            label: 'Overview',
            children: (
              <>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title="Positions value"
                        value={Number(holdingsData?.positions_value ?? summary.valuation ?? 0)}
                        precision={4}
                        suffix={reportingCcy}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title="Cash weight"
                        value={Number(holdingsData?.cash_weight ?? 0)}
                        precision={4}
                        suffix="%"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title="Holdings"
                        value={(holdingsData?.holdings || []).length}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                      <Statistic title="Pending settlements" value={summary.pending_settlements} />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Instrument trades not yet settled
                      </Text>
                    </Card>
                  </Col>
                </Row>

                {summary.fx_missing_pairs?.length > 0 && (
                  <Alert
                    style={{ marginTop: 16 }}
                    type="warning"
                    showIcon
                    message="Some FX rates are missing; valuation may be approximate."
                    description={summary.fx_missing_pairs.join(', ')}
                  />
                )}

                <Card style={{ marginTop: 16 }} title="Holdings">
                  <Table
                    size="small"
                    loading={holdingsLoading}
                    rowKey="key"
                    columns={holdingsColumns}
                    dataSource={holdingsTableRows}
                    pagination={false}
                    locale={{ emptyText: 'No holdings yet.' }}
                    rowClassName={(row) => (row.isCash ? 'portfolio-cash-row' : '')}
                  />
                </Card>

                <Card style={{ marginTop: 16 }} title="Portfolio details">
                  <Descriptions
                    size="small"
                    column={{ xs: 1, sm: 2, md: 3 }}
                    items={[
                      {
                        key: 'account',
                        label: 'Account',
                        children: portfolio.account
                          ? `${portfolio.account.name} (${portfolio.account.account_number || '—'})`
                          : '—',
                      },
                      {
                        key: 'ccy',
                        label: 'Reporting currency',
                        children: reportingCcy || '—',
                      },
                      {
                        key: 'model',
                        label: 'Model portfolio',
                        children:
                          portfolio.model_portfolio != null
                            ? `Linked model #${portfolio.model_portfolio}`
                            : '—',
                      },
                      {
                        key: 'risk',
                        label: 'Risk profile',
                        children: portfolio.risk_profile || '—',
                      },
                      {
                        key: 'rebalance',
                        label: 'Rebalancing',
                        children: (
                          <Space size={8}>
                            <Tag color={portfolio.rebalance_enabled ? 'green' : 'default'}>
                              {portfolio.rebalance_enabled ? 'Enabled' : 'Disabled'}
                            </Tag>
                            {portfolio.default_tolerance != null && (
                              <Text type="secondary">
                                Tolerance {formatNumber(portfolio.default_tolerance, 4)}%
                              </Text>
                            )}
                          </Space>
                        ),
                      },
                      {
                        key: 'mincash',
                        label: 'Minimum cash',
                        children:
                          portfolio.minimum_cash_percentage != null
                            ? `${formatNumber(portfolio.minimum_cash_percentage, 4)}%`
                            : '—',
                      },
                      {
                        key: 'custodian',
                        label: 'Custodian',
                        children: (portfolio.custodian || '—').toString().toUpperCase(),
                      },
                      {
                        key: 'custodian_code',
                        label: 'Custodian account',
                        children: portfolio.custodian_account_code || '—',
                      },
                      {
                        key: 'unitized',
                        label: 'Unitization',
                        children: (
                          <Space size={8} wrap>
                            <Tag color={summary.unitized || portfolio.unitized ? 'blue' : 'default'}>
                              {summary.unitized || portfolio.unitized ? 'Unitized' : 'Not unitized'}
                            </Tag>
                            {(summary.nav_isin || portfolio.nav_instrument) && (
                              <Text type="secondary">
                                Feed ISIN: {summary.nav_isin || '—'}
                                {summary.nav_instrument_id
                                  ? ` · #${summary.nav_instrument_id}`
                                  : ''}
                              </Text>
                            )}
                          </Space>
                        ),
                      },
                    ]}
                  />
                </Card>

                <Card style={{ marginTop: 16 }} title="Unit NAV (external product feed)">
                  {summary.unitized || portfolio.unitized ? (
                    <>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        Use Subscribe / Redeem for capital flows, or the Subscriptions / Redemptions
                        tab for the full ledger. Cash updates when a flow is settled.
                      </Text>
                      <Space>
                        <Button type="primary" onClick={() => openCapitalModal('subscribe')}>
                          Subscribe
                        </Button>
                        <Button onClick={() => openCapitalModal('redeem')}>Redeem</Button>
                      </Space>
                    </>
                  ) : (
                    <>
                      <Text type="secondary">
                        Enable unitization to publish a unit NAV so another product can invest at
                        this portfolio&apos;s price. Total NAV above already includes positions + cash.
                      </Text>
                      <div style={{ marginTop: 12 }}>
                        <Popconfirm
                          title="Enable unitization?"
                          description="Units will be seeded at base NAV 100 from current total assets."
                          onConfirm={handleUnitize}
                          okText="Enable"
                        >
                          <Button type="primary" loading={unitizing}>
                            Enable unitization
                          </Button>
                        </Popconfirm>
                      </div>
                    </>
                  )}
                </Card>

                <Modal
                  title={capitalModal === 'subscribe' ? 'Subscription' : 'Redemption'}
                  open={Boolean(capitalModal)}
                  onCancel={() => setCapitalModal(null)}
                  onOk={handleCapitalSubmit}
                  confirmLoading={capitalSaving}
                  destroyOnClose
                >
                  {capitalModal === 'redeem' && liquidityInfo?.requires_notice && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Illiquid holdings require notice"
                      description={
                        <>
                          Earliest dealing date:{' '}
                          <Text strong>
                            {formatDisplayDate(liquidityInfo.earliest_redemption_date)}
                          </Text>
                          {' · '}
                          {liquidityInfo.max_notice_period_days}d notice
                          {liquidityInfo.strictest_dealing_frequency
                            ? ` · ${humanizeCode(liquidityInfo.strictest_dealing_frequency)} dealing`
                            : ''}
                          . Redemption will be booked for that dealing date, not T+0 NAV.
                        </>
                      }
                    />
                  )}
                  <Form form={capitalForm} layout="vertical" initialValues={{ redeem_by: 'units' }}>
                    {capitalModal === 'subscribe' ? (
                      <Form.Item
                        name="amount"
                        label={`Amount (${reportingCcy})`}
                        rules={[{ required: true, message: 'Enter subscription amount' }]}
                      >
                        <InputNumber min={0.01} step={100} style={{ width: '100%' }} />
                      </Form.Item>
                    ) : (
                      <>
                        <Form.Item name="redeem_by" label="Redeem by">
                          <Select
                            options={[
                              { value: 'units', label: 'Units' },
                              { value: 'amount', label: 'Amount' },
                            ]}
                          />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.redeem_by !== cur.redeem_by}>
                          {() =>
                            capitalForm.getFieldValue('redeem_by') === 'amount' ? (
                              <Form.Item
                                name="amount"
                                label={`Amount (${reportingCcy})`}
                                rules={[{ required: true, message: 'Enter redemption amount' }]}
                              >
                                <InputNumber min={0.01} step={100} style={{ width: '100%' }} />
                              </Form.Item>
                            ) : (
                              <Form.Item
                                name="units"
                                label="Units"
                                rules={[{ required: true, message: 'Enter units to redeem' }]}
                              >
                                <InputNumber min={0.000001} step={1} style={{ width: '100%' }} />
                              </Form.Item>
                            )
                          }
                        </Form.Item>
                      </>
                    )}
                    <Form.Item name="reference" label="Reference (optional)">
                      <Input maxLength={120} />
                    </Form.Item>
                  </Form>
                </Modal>

                <Card style={{ marginTop: 16 }} title="Custodian Mapping">
                  <Row gutter={[12, 12]}>
                    <Col xs={24} md={8}>
                      <Select
                        value={custodianDraft}
                        onChange={setCustodianDraft}
                        options={[
                          { value: 'mcb', label: 'MCB' },
                          { value: 'other', label: 'Other' },
                        ]}
                        style={{ width: '100%' }}
                      />
                    </Col>
                    <Col xs={24} md={10}>
                      <Input
                        value={custodianCodeDraft}
                        onChange={(e) => setCustodianCodeDraft(e.target.value)}
                        placeholder="Custodian account code (e.g. 1916184-1)"
                      />
                    </Col>
                    <Col xs={24} md={6}>
                      <Button type="primary" loading={savingCustodian} block onClick={handleSaveCustodian}>
                        Save Mapping
                      </Button>
                    </Col>
                  </Row>
                </Card>
              </>
            ),
          },
          {
            key: 'transactions',
            label: 'Transactions',
            children: (
              <TradeList
                fixedPortfolioId={Number(id)}
                fixedAccountId={portfolio.account?.id}
                embedded
                title="Transactions"
              />
            ),
          },
          {
            key: 'capital-flows',
            label: 'Subscriptions / Redemptions',
            children: (
              <Card>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                  <Col>
                    <Title level={5} style={{ margin: 0 }}>
                      Capital flows
                    </Title>
                    <Text type="secondary">
                      Separate from instrument trades. Cash moves only when settled.
                    </Text>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        type="primary"
                        disabled={!(summary.unitized || portfolio.unitized)}
                        onClick={() => openCapitalModal('subscribe')}
                      >
                        Subscribe
                      </Button>
                      <Button
                        disabled={!(summary.unitized || portfolio.unitized)}
                        onClick={() => openCapitalModal('redeem')}
                      >
                        Redeem
                      </Button>
                      <Button onClick={loadCapitalFlows} loading={capitalFlowsLoading}>
                        Refresh
                      </Button>
                    </Space>
                  </Col>
                </Row>
                {!(summary.unitized || portfolio.unitized) ? (
                  <Alert
                    type="info"
                    showIcon
                    message="Enable unitization on Overview to create subscriptions and redemptions."
                  />
                ) : (
                  <Table
                    rowKey="id"
                    loading={capitalFlowsLoading}
                    dataSource={capitalFlows}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      {
                        title: 'Type',
                        dataIndex: 'flow_type',
                        render: (v) => (
                          <Tag color={v === 'subscription' ? 'green' : 'orange'}>
                            {v === 'subscription' ? 'Subscription' : 'Redemption'}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Status',
                        dataIndex: 'status',
                        render: (v) => (
                          <Tag
                            color={
                              v === 'settled' ? 'blue' : v === 'pending' ? 'gold' : 'default'
                            }
                          >
                            {(v || '').toUpperCase()}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Amount',
                        dataIndex: 'amount',
                        align: 'right',
                        render: (v) =>
                          `${Number(v).toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          })} ${summary.reporting_currency}`,
                      },
                      {
                        title: 'Units',
                        dataIndex: 'units',
                        align: 'right',
                        render: (v) =>
                          Number(v).toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          }),
                      },
                      {
                        title: 'NAV/unit',
                        dataIndex: 'nav_per_unit',
                        align: 'right',
                        render: (v) =>
                          Number(v).toLocaleString(undefined, {
                            minimumFractionDigits: 4,
                            maximumFractionDigits: 4,
                          }),
                      },
                      {
                        title: 'Effective',
                        dataIndex: 'effective_date',
                        render: (v) => formatDisplayDate(v),
                      },
                      {
                        title: 'Reference',
                        dataIndex: 'reference',
                        render: (v) => v || '—',
                      },
                      {
                        title: 'Actions',
                        key: 'actions',
                        render: (_, row) =>
                          row.status === 'pending' ? (
                            <Space>
                              <Popconfirm
                                title="Settle this flow?"
                                description={
                                  row.flow_type === 'subscription'
                                    ? 'Cash will increase and units will be issued.'
                                    : 'Cash will decrease and units will be cancelled.'
                                }
                                onConfirm={() => handleSettleFlow(row.id)}
                              >
                                <Button
                                  type="primary"
                                  size="small"
                                  loading={Boolean(flowActionBusy[row.id])}
                                >
                                  Settle
                                </Button>
                              </Popconfirm>
                              <Popconfirm
                                title="Cancel this pending flow?"
                                onConfirm={() => handleCancelFlow(row.id)}
                              >
                                <Button size="small" danger loading={Boolean(flowActionBusy[row.id])}>
                                  Cancel
                                </Button>
                              </Popconfirm>
                            </Space>
                          ) : (
                            '—'
                          ),
                      },
                    ]}
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'rebalance',
            label: 'Rebalance',
            children: <PortfolioRebalancePanel portfolioId={id} />,
          },
          {
            key: 'target-allocation',
            label: 'Model / Rules',
            children: (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={10}>
                    <Select
                      showSearch
                      placeholder="Select instrument"
                      loading={instrumentLoading}
                      value={newInstrumentId}
                      onChange={setNewInstrumentId}
                      options={selectableInstruments.map((instrument) => ({
                        value: instrument.id,
                        label: `${instrument.name} (${instrument.isin || instrument.symbol})`,
                      }))}
                      optionFilterProp="label"
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.01}
                      value={newTargetPercentage}
                      onChange={setNewTargetPercentage}
                      placeholder="Target %"
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.01}
                      value={newMinPercentage}
                      onChange={setNewMinPercentage}
                      placeholder="Min %"
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={4}>
                    <InputNumber
                      min={0}
                      max={100}
                      step={0.01}
                      value={newMaxPercentage}
                      onChange={setNewMaxPercentage}
                      placeholder="Max %"
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Button
                      type="primary"
                      onClick={handleAddAllocation}
                      loading={savingNewRow}
                      block
                    >
                      Add allocation rule
                    </Button>
                  </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={8}>
                    <Card size="small">
                      <Statistic
                        title="Total target"
                        value={allocationTotal}
                        precision={4}
                        suffix="%"
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={16}>
                    {isPartialAllocation ? (
                      <Alert
                        showIcon
                        type="warning"
                        message="Target allocation is partial"
                        description="Total target is below 100%. This is allowed in the current phase."
                      />
                    ) : (
                      <Tag color="#FF6600" style={{ marginTop: 8 }}>
                        Target total is at or above 100%
                      </Tag>
                    )}
                  </Col>
                </Row>

                <Table
                  rowKey="id"
                  loading={allocationLoading}
                  columns={allocationColumns}
                  dataSource={allocations}
                  pagination={false}
                  locale={{
                    emptyText: 'No target allocation rules yet. Add your first instrument target above.',
                  }}
                />
              </>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default PortfolioDetail;
